import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ProfileSettings from '../Profile/ProfileSettings';
import { authFetch } from '../../utils/authFetch';

interface SidebarProps {
  activeSection: 'inbox' | 'contacts' | 'quick-replies' | 'templates' | 'reports' | 'settings';
  onSectionChange: (section: 'inbox' | 'contacts' | 'quick-replies' | 'templates' | 'reports' | 'settings') => void;
  unreadCount?: number;
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, unreadCount, className = '' }) => {
  const { user, logout } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [businessName, setBusinessName] = useState<string>('WhatsApp Chat');
  useEffect(() => {
    const loadBusinessName = async () => {
      try {
        const response = await authFetch('/settings/whatsapp');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.displayName) {
            setBusinessName(data.data.displayName);
          }
        }
      } catch (error) {
        // Not logged in yet or error, use default
      }
    };
    if (user) {
      loadBusinessName();
    }
  }, [user]);

  const navigationItems = [
    {
      key: 'inbox',
      label: 'Conversations',
      icon: '💬',
      badge: unreadCount
    },
    {
      key: 'contacts',
      label: 'Contacts',
      icon: '👥',
    },
    {
      key: 'quick-replies',
      label: 'Quick Replies',
      icon: '⚡',
    },
    {
      key: 'templates',
      label: 'Templates',
      icon: '📋',
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: '📊',
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: '⚙️',
    }
  ];

  return (
    <div className={`navigation-sidebar ${className}`}>
      <div className="navigation-header">
        <div className="logo">
          <span className="logo-text">{businessName}</span>
        </div>
      </div>
      
      <nav className="navigation-menu">
        {navigationItems.map((item) => (
          <button
            key={item.key}
            className={`nav-item ${activeSection === item.key ? 'active' : ''}`}
            onClick={() => onSectionChange(item.key as any)}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
            {item.badge && item.badge > 0 && (
              <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="navigation-footer">
        <div className="user-info">
          <div 
            className="user-profile-section"
            onClick={() => setShowProfileModal(true)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              cursor: 'pointer',
              flex: 1,
              padding: '4px',
              borderRadius: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="user-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <span>👤</span>
              )}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name || 'Agent'}</div>
              <div className="user-status">
                <span style={{ color: '#10b981' }}>● Online</span>
              </div>
            </div>
          </div>
          <button 
            onClick={logout}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '12px',
              padding: '4px'
            }}
            title="Logout"
          >
            ⏻
          </button>
        </div>
        
        {showProfileModal && (
          <div className="assignment-modal-overlay" onClick={() => setShowProfileModal(false)}>
            <div className="assignment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="assignment-header">
                <h4>Profile Settings</h4>
                <button onClick={() => setShowProfileModal(false)} className="close-button">×</button>
              </div>
              <div className="assignment-content">
                <ProfileSettings />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
